/**
 * `/account`, the fan's own account home. Wires the overlay URL state for
 * R4's award chip sheet and the sign-out handler, and mounts the pane. No
 * data fetching of its own; `AccountHomePane` owns the three reads.
 */
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { requireSignedIn } from "../auth/requireSignedIn";
import { ACCOUNT_HEADING } from "../features/accountHome/accountHomeCopy";
import { AccountHomePane } from "../features/accountHome/AccountHomePane";
import { AwardChipDialog } from "../features/accountHome/AwardChipDialog";
import type { typeAwardChip } from "../features/awards/awardChips";
import { closeOverlay } from "../routeState/overlay";
import { validateAccountSearch } from "../routeState/sectionSearch";
import { SectionScreen } from "../shell/SectionScreen";

const AWARD_CHIP_SHEET = "award-chip";

const AccountScreen = (): ReactNode => {
  const search = useSearch({ from: "/account" });
  const navigate = useNavigate({ from: "/account" });
  const router = useRouter();
  const { signOut, isSigningOut } = useAuth();
  const [selectedChip, setSelectedChip] = useState<{
    chip: typeAwardChip;
    poolName: string | null;
  } | null>(null);

  return (
    <SectionScreen heading={ACCOUNT_HEADING}>
      <AccountHomePane
        onChipSelect={(input) => {
          setSelectedChip(input);
          void navigate({
            search: (prev) => ({
              ...prev,
              sheet: AWARD_CHIP_SHEET,
              awardGrantId: input.chip.awardGrantId,
            }),
          });
        }}
        onSignOut={() => {
          void signOut().then(() => router.invalidate());
        }}
        isSigningOut={isSigningOut}
      />
      <AwardChipDialog
        open={search.sheet === AWARD_CHIP_SHEET && selectedChip !== null}
        onClose={() => {
          setSelectedChip(null);
          void navigate({
            search: (prev) =>
              closeOverlay(prev, {
                kind: "sheet",
                clearParams: ["awardGrantId"],
              }),
            replace: true,
          });
        }}
        chip={selectedChip?.chip ?? null}
        poolName={selectedChip?.poolName ?? null}
        testId="account-award-sheet"
      />
    </SectionScreen>
  );
};

export const Route = createFileRoute("/account")({
  beforeLoad: requireSignedIn,
  validateSearch: validateAccountSearch,
  component: AccountScreen,
});
